// src/admin/BorrowerManagementPage.jsx
import React from "react";
import {
  Search,
  Filter,
  Eye,
  EyeOff,
  Edit2,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  User,
  MapPin,
  Calendar,
  Briefcase,
  Home,
  DollarSign,
  FileText,
  AlertCircle,
  Download,
  CreditCard,
  Building,
  Hash,
  Lock,
  Copy,
  RefreshCw,
} from "lucide-react";
import { TableSkeleton } from "../../components/ui/Skeleton.jsx";
import { adminDeleteUser, adminListUsers } from "../../api/admin/adminListUsers.js";

/**
 * Enhanced Borrower Management for Dynesourcer:
 * ✅ Table (ID, Name, Phone, Email, Status, Actions)
 * ✅ Search & Filter
 * ✅ View Borrower (comprehensive details)
 * ✅ Edit Borrower (update details)
 * ✅ Export to CSV
 * ✅ Detailed borrower information display
 *
 * NOTE: Borrowers sign up themselves, so no "Add Borrower" function.
 * Admin can only view, edit, block/unblock, and manage existing borrowers.
 */

const SYSTEM_ROLES = new Set([
  "All",
  "Guest",
  "Desk User",
  "Website User",
  "System Manager",
  "Administrator",
]);

const ADMIN_ROLES = new Set(["Administrator", "System Manager", "Admin"]);

/** Roles from admin_list_users (string, CSV, or [{ role }]). */
function collectRawRoles(row) {
  const collect = (value) => {
    if (value == null || value === "") return [];
    if (Array.isArray(value)) {
      return value.flatMap((item) => {
        if (typeof item === "string") return [item];
        return [item?.role, item?.name, item?.role_name].filter(Boolean);
      });
    }
    if (typeof value === "string") {
      return value.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
    }
    if (typeof value === "object") {
      return [value.role, value.name].filter(Boolean);
    }
    return [];
  };

  const names = [
    ...collect(row.roles),
    ...collect(row.user_roles),
    ...collect(row.role),
  ]
    .map((s) => String(s).trim())
    .filter(Boolean);

  return [...new Set(names)];
}

function userHasAdminRole(roles) {
  return roles.some(
    (role) =>
      ADMIN_ROLES.has(role) ||
      role.toLowerCase() === "administrator" ||
      role.toLowerCase() === "system manager"
  );
}

function userHasBorrowerRole(roles) {
  return roles.some((role) => role.toLowerCase() === "borrower");
}

/** Borrower management should not list admin-only accounts. */
function isBorrowerManagementUser(row) {
  const roles = collectRawRoles(row);
  if (userHasAdminRole(roles) && !userHasBorrowerRole(roles)) return false;
  return true;
}

function extractUserRoles(row) {
  const all = collectRawRoles(row);
  const businessRoles = all.filter((role) => !SYSTEM_ROLES.has(role));
  const uniqueBusiness = [...new Set(businessRoles)];

  if (uniqueBusiness.length) {
    if (userHasAdminRole(all) && !uniqueBusiness.some((r) => /admin/i.test(r))) {
      return ["Admin", ...uniqueBusiness];
    }
    return uniqueBusiness;
  }

  if (userHasAdminRole(all)) return ["Admin"];
  return ["Borrower"];
}

function formatUserRoles(roles) {
  return Array.isArray(roles) && roles.length ? roles.join(", ") : "—";
}

const employmentStatuses = ["Employed", "Self-Employed", "Retired", "Unemployed", "Student"];
const ukCities = [
  "London",
  "Manchester",
  "Birmingham",
  "Edinburgh",
  "Glasgow",
  "Bristol",
  "Leeds",
  "Liverpool",
  "Newcastle",
  "Sheffield",
  "Cardiff",
  "Belfast",
];

function StatusPill({ status, tone }) {
  const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium";
  if (tone === "red") {
    return (
      <span className={`${base} bg-red-100 text-red-700`}>
        <XCircle className="h-4 w-4" />
        {status}
      </span>
    );
  }
  return (
    <span className={`${base} bg-sky-100 text-sky-700`}>
      <CheckCircle2 className="h-4 w-4" />
      {status}
    </span>
  );
}

function rolePillTone(role) {
  const r = String(role || "").toLowerCase();
  if (r.includes("lender")) return "emerald";
  if (r.includes("admin") || r.includes("system")) return "violet";
  if (r.includes("borrower")) return "sky";
  return "slate";
}

function RolePill({ role }) {
  const tone = rolePillTone(role);
  const styles = {
    sky: "bg-sky-100 text-sky-700",
    emerald: "bg-emerald-100 text-emerald-700",
    violet: "bg-violet-100 text-violet-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${styles[tone]}`}
    >
      <User className="h-3.5 w-3.5" />
      {role}
    </span>
  );
}

function UserRolesCell({ roles }) {
  const list = Array.isArray(roles) && roles.length ? roles : [];
  if (!list.length) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {list.map((role) => (
        <RolePill key={role} role={role} />
      ))}
    </div>
  );
}

function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">{children}</div>
          <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-slate-600">{label}</div>
      <div className="mt-1 relative">
        {Icon ? <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /> : null}
        <div className={Icon ? "pl-9" : ""}>{children}</div>
      </div>
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text", disabled = false, step }) {
  return (
    <input
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      step={step}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function Textarea({ value, onChange, placeholder, disabled = false, rows = 3 }) {
  return (
    <textarea
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function Select({ value, onChange, options, disabled = false }) {
  return (
    <div className="relative">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2 pr-9 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

function slugifyUsername(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 24);
}

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  let out = "";
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function BorrowerManagementPage() {
  const [borrowers, setBorrowers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [modal, setModal] = React.useState({ open: false, mode: "view", borrower: null });
  const [draft, setDraft] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [flashPassword, setFlashPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const filtered = React.useMemo(() => {
    return borrowers.filter((b) => statusFilter === "All" || b.status === statusFilter);
  }, [borrowers, statusFilter]);

  // Notification Handler
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchBorrowers = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminListUsers({
        limit_start: 0,
        limit_page_length: 100,
        role: "Borrower",
        exclude_system_manager: 1,
        search: q.trim() || undefined,
      });
      const firstPage = Array.isArray(data) ? data : [];
      setBorrowers(
        firstPage.filter(isBorrowerManagementUser).map((row) => {
          const firstLast =
            [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || null;
          const name = row.full_name ?? firstLast ?? row.email ?? row.name ?? "—";
          const email = row.email ?? row.name ?? "";
          const enabled = row.enabled !== false && row.enabled !== 0;
          const roles = extractUserRoles(row);
          return {
            id: row.name ?? row.email ?? String(row.id ?? ""),
            name,
            phone: row.mobile_no ?? row.phone ?? "",
            email,
            roles,
            userRole: formatUserRoles(roles),
            status: enabled ? "Active" : "Blocked",
            tone: enabled ? "blue" : "red",
            registeredDate: row.creation ? row.creation.split(" ")[0] : "",
            lastLogin: row.modified ? row.modified.split(" ")[0] : "",
          };
        })
      );
    } catch (err) {
      console.error("Failed to fetch borrowers:", err);
      setBorrowers([]);
      showNotification("Failed to load borrowers.", "error");
    } finally {
      setLoading(false);
    }
  }, [q]);

  React.useEffect(() => {
    fetchBorrowers();
  }, [fetchBorrowers]);

  const debouncedSearch = React.useRef(null);
  React.useEffect(() => {
    if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    debouncedSearch.current = setTimeout(() => setQ(searchInput), 300);
    return () => {
      if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    };
  }, [searchInput]);

  const openView = (b) => {
    setDraft({ ...b });
    setModal({ open: true, mode: "view", borrower: b });
  };

  const openEdit = (b) => {
    setDraft({ ...b });
    setModal({ open: true, mode: "edit", borrower: b });
  };

  const closeModal = () => {
    setModal({ open: false, mode: "view", borrower: null });
    setDraft(null);
    setFlashPassword("");
    setShowPassword(false);
  };

  const saveEdit = () => {
    if (!draft?.name?.trim()) {
      showNotification("Borrower name is required", "error");
      return;
    }
    if (!draft?.email?.trim()) {
      showNotification("Email is required", "error");
      return;
    }

    setBorrowers((prev) => prev.map((b) => (b.id === draft.id ? { ...draft } : b)));
    showNotification("Borrower updated successfully!");
    closeModal();
  };

  const deleteBorrower = async (borrower) => {
    const userIdentifier = borrower.email ?? borrower.id;
    if (!window.confirm(`Are you sure you want to delete ${borrower.name}? This cannot be undone.`)) return;
    try {
      const result = await adminDeleteUser(userIdentifier);
      if (result.ok) {
        setBorrowers((prev) => prev.filter((b) => (b.email ?? b.id) !== userIdentifier));
        showNotification("Borrower deleted successfully!");
      } else {
        showNotification(result.error || "Failed to delete borrower", "error");
      }
    } catch {
      showNotification("Failed to delete borrower", "error");
    }
  };

  const resetPassword = () => {
    const pwd = genPassword();
    setDraft((d) => ({ ...d, password: pwd }));
    setFlashPassword(pwd);
    showNotification("New password generated successfully!");
  };

  const handleExport = () => {
    const csv = [
      [
        "Borrower ID",
        "Name",
        "Email",
        "Phone",
        "User",
        "Status",
        "City",
        "Employment",
        "Annual Income",
        "Credit Score",
        "Properties",
        "Applications",
      ],
      ...filtered.map((b) => [
        b.id,
        b.name,
        b.email,
        b.phone,
        b.userRole || "",
        b.status,
        b.city || "",
        b.employmentStatus || "",
        b.annualIncome || "",
        b.creditScore || "",
        b.propertyOwned || "",
        b.applicationCount || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "borrowers_export.csv";
    a.click();
    showNotification("Borrowers exported successfully!");
  };

  return (
    <div className="space-y-4">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border shadow-lg flex items-center gap-2 animate-slide-in ${
            notification.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {notification.type === "success" ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span className="text-xs font-medium">{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-sky-50 flex items-center justify-center">
            <User className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-sm md:text-base font-semibold text-slate-900">Borrower Management</h1>
            <p className="text-[11px] text-slate-500">
              View and manage borrower accounts (borrowers sign up themselves)
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50"
            title="Export to CSV"
          >
            <Download className="h-4 w-4" />
            Export
          </button>

          <div className="hidden md:flex items-center px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search borrowers..."
              className="bg-transparent text-xs outline-none w-44"
            />
            {searchInput ? (
              <button onClick={() => setSearchInput("")} className="text-slate-400 hover:text-slate-700" title="Clear">
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 bg-white">
            <Filter className="h-4 w-4 text-slate-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent outline-none text-xs"
            >
              <option>All</option>
              <option>Active</option>
              <option>Blocked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mobile search */}
      <div className="md:hidden bg-white rounded-2xl border border-slate-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search borrowers..."
            className="bg-transparent text-xs outline-none w-full"
          />
          {searchInput ? (
            <button onClick={() => setSearchInput("")} className="text-slate-400 hover:text-slate-700" title="Clear">
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Borrower ID</th>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Phone</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">User</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableSkeleton rows={8} cols={7} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                  No borrowers found.
                </td>
              </tr>
            ) : (
              filtered.map((b) => (
              <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40">
                <td className="px-4 py-3 text-slate-900">{b.id}</td>
                <td className="px-4 py-3 text-slate-800">{b.name}</td>
                <td className="px-4 py-3 text-slate-800">{b.phone}</td>
                <td className="px-4 py-3 text-slate-800">{b.email}</td>
                <td className="px-4 py-3">
                  <UserRolesCell roles={b.roles} />
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={b.status} tone={b.tone} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 text-slate-700">
                    <button
                      className="hover:text-sky-500"
                      onClick={() => openEdit(b)}
                      title="Edit borrower"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      className="hover:text-sky-500"
                      onClick={() => openView(b)}
                      title="View borrower"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      className="hover:text-red-500"
                      onClick={() => deleteBorrower(b)}
                      title="Delete borrower"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Modal
        open={modal.open}
        title={modal.mode === "edit" ? "Edit Borrower" : "View Borrower"}
        onClose={closeModal}
        footer={
          <div className="w-full flex items-center justify-between">
            <button
              onClick={closeModal}
              className="px-3 py-2 rounded-xl text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>

            {modal.mode === "edit" && (
              <button
                onClick={saveEdit}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-sky-500 text-white hover:bg-sky-600"
              >
                Save changes
              </button>
            )}
          </div>
        }
      >
        {!draft ? null : (
          <div className="space-y-4">
            {/* Personal Information Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                Personal Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Borrower ID" icon={Hash}>
                  <Input value={draft.id} onChange={() => {}} disabled />
                </Field>

                <Field label="Status" icon={Filter}>
                  <div className="flex items-center h-[34px]">
                    <StatusPill status={draft.status} tone={draft.tone} />
                  </div>
                </Field>

                <Field label="User" icon={User}>
                  <div className="flex items-center min-h-[34px]">
                    <UserRolesCell roles={draft.roles} />
                  </div>
                </Field>

                <Field label="Full Name *" icon={User}>
                  <Input
                    value={draft.name}
                    onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
                    placeholder="e.g. John Smith"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Date of Birth" icon={Calendar}>
                  <Input
                    type="date"
                    value={draft.dateOfBirth || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, dateOfBirth: v }))}
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Email *" icon={Mail}>
                  <Input
                    value={draft.email}
                    onChange={(v) => setDraft((d) => ({ ...d, email: v }))}
                    placeholder="borrower@email.com"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Phone *" icon={Phone}>
                  <Input
                    value={draft.phone}
                    onChange={(v) => setDraft((d) => ({ ...d, phone: v }))}
                    placeholder="07700 900111"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Nationality" icon={User}>
                  <Input
                    value={draft.nationality || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, nationality: v }))}
                    placeholder="e.g. British"
                    disabled={modal.mode === "view"}
                  />
                </Field>
              </div>
            </div>

            {/* Login Credentials Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-slate-500" />
                Login Credentials
              </div>

              {/* Flash password notification */}
              {flashPassword && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <div className="font-semibold mb-1">New Password Generated</div>
                  <div className="flex items-center justify-between gap-2">
                    <code className="px-2 py-1 rounded-xl border border-amber-200 bg-white text-[11px]">
                      {flashPassword}
                    </code>
                    <button
                      onClick={() => navigator.clipboard?.writeText(flashPassword)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
                      type="button"
                    >
                      <Copy className="h-4 w-4" />
                      Copy
                    </button>
                  </div>
                  <div className="mt-1 text-[11px] text-amber-700">
                    Share this password securely with the borrower. Store only hashed passwords in backend.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Username *" icon={User}>
                  <Input
                    value={draft.username || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, username: slugifyUsername(v) }))}
                    placeholder="e.g. johnsmith"
                    disabled={modal.mode === "view"}
                  />
                  {modal.mode === "edit" && (
                    <div className="mt-1 text-xs text-slate-500">
                      Username will be auto-generated from name if left empty
                    </div>
                  )}
                </Field>

                {modal.mode === "edit" && (
                  <div className="flex items-end">
                    <button
                      onClick={resetPassword}
                      type="button"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <RefreshCw className="h-4 w-4 text-slate-500" />
                      Reset Password
                    </button>
                  </div>
                )}
              </div>

              {modal.mode === "edit" && draft.password && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="New Password (generated)" icon={Lock}>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={draft.password || ""}
                        onChange={(v) => setDraft((d) => ({ ...d, password: v }))}
                        placeholder="Generated password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </Field>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(draft.password || "")}
                      disabled={!draft.password}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-sky-500 text-white text-xs font-medium hover:bg-sky-600 disabled:opacity-50"
                    >
                      <Copy className="h-4 w-4" />
                      Copy Password
                    </button>
                  </div>
                </div>
              )}

              {modal.mode === "view" && (
                <div className="mt-2 text-xs text-slate-500">
                  Password is hidden for security. Use Edit mode to reset password.
                </div>
              )}
            </div>

            {/* Address Information Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Home className="h-4 w-4 text-slate-500" />
                Address Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Address" icon={MapPin} className="md:col-span-2">
                  <Input
                    value={draft.address || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, address: v }))}
                    placeholder="Full address"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="City" icon={MapPin}>
                  <Select
                    value={draft.city || "London"}
                    onChange={(v) => setDraft((d) => ({ ...d, city: v }))}
                    disabled={modal.mode === "view"}
                    options={ukCities.map((c) => ({ label: c, value: c }))}
                  />
                </Field>

                <Field label="Postcode" icon={MapPin}>
                  <Input
                    value={draft.postcode || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, postcode: v }))}
                    placeholder="e.g. W1K 1PN"
                    disabled={modal.mode === "view"}
                  />
                </Field>
              </div>
            </div>

            {/* Employment & Financial Information Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-slate-500" />
                Employment & Financial Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Employment Status" icon={Briefcase}>
                  <Select
                    value={draft.employmentStatus || "Employed"}
                    onChange={(v) => setDraft((d) => ({ ...d, employmentStatus: v }))}
                    disabled={modal.mode === "view"}
                    options={employmentStatuses.map((s) => ({ label: s, value: s }))}
                  />
                </Field>

                <Field label="Employer" icon={Building}>
                  <Input
                    value={draft.employer || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, employer: v }))}
                    placeholder="Company name"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Annual Income (£)" icon={DollarSign}>
                  <Input
                    type="number"
                    value={String(draft.annualIncome ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, annualIncome: Number(v || 0) }))}
                    placeholder="e.g. 75000"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Credit Score" icon={CreditCard}>
                  <Input
                    type="number"
                    value={String(draft.creditScore ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, creditScore: Number(v || 0) }))}
                    placeholder="e.g. 720"
                    disabled={modal.mode === "view"}
                  />
                </Field>
              </div>
            </div>

            {/* Property & Loan Information Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Home className="h-4 w-4 text-slate-500" />
                Property & Loan Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Existing Mortgages" icon={Home}>
                  <Input
                    type="number"
                    value={String(draft.existingMortgages ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, existingMortgages: Number(v || 0) }))}
                    placeholder="0"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Properties Owned" icon={Building}>
                  <Input
                    type="number"
                    value={String(draft.propertyOwned ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, propertyOwned: Number(v || 0) }))}
                    placeholder="0"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Total Applications" icon={FileText}>
                  <Input
                    type="number"
                    value={String(draft.applicationCount ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, applicationCount: Number(v || 0) }))}
                    placeholder="0"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Approved Loans" icon={CheckCircle2}>
                  <Input
                    type="number"
                    value={String(draft.approvedLoans ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, approvedLoans: Number(v || 0) }))}
                    placeholder="0"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Rejected Loans" icon={XCircle}>
                  <Input
                    type="number"
                    value={String(draft.rejectedLoans ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, rejectedLoans: Number(v || 0) }))}
                    placeholder="0"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Total Borrowed (£)" icon={DollarSign}>
                  <Input
                    type="number"
                    value={String(draft.totalBorrowed ?? 0)}
                    onChange={(v) => setDraft((d) => ({ ...d, totalBorrowed: Number(v || 0) }))}
                    placeholder="0"
                    disabled={modal.mode === "view"}
                  />
                </Field>
              </div>
            </div>

            {/* Account Information Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                Account Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Registered Date" icon={Calendar}>
                  <Input type="date" value={draft.registeredDate || ""} onChange={() => {}} disabled />
                </Field>

                <Field label="Last Login" icon={Calendar}>
                  <Input type="date" value={draft.lastLogin || ""} onChange={() => {}} disabled />
                </Field>
              </div>
            </div>

            {/* Notes Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                Admin Notes
              </div>
              <Field label="Notes" icon={FileText}>
                <Textarea
                  value={draft.notes || ""}
                  onChange={(v) => setDraft((d) => ({ ...d, notes: v }))}
                  placeholder="Add any admin notes about this borrower..."
                  disabled={modal.mode === "view"}
                  rows={4}
                />
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}